import { Component, forwardRef, inject, OnInit, Provider } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { FormGroupComponent, FormsUtil, InputComponent, ModalService, SwitcherComponent } from 'mbs-ui-kit';
import { combineLatest, filter, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  DefaultTemplate,
  DefaultTemplateMask,
  DestinationDBStepValue,
  DestinationDBStepValueForm
} from 'wizards/models/destination-db-models';
import { DatabaseItem } from 'wizards/models/sql-source-databases-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { RestoreSourceData, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { DestinationTableData } from 'wizards/steps/components/destination-table/destination-table.component';
import { BaseForDestinationStep } from 'wizards/steps/only-restore-steps/base/base-for-destination-step';

import { DestinationTableComponent } from 'wizards/steps/components/destination-table/destination-table.component';

import { DestinationFolderInputComponent } from 'mbs-ui-shared@components/destination-folder-input/destination-folder-input.component';

import { CommonModule } from '@angular/common';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

const DestinationDBStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => DestinationDBStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-destination-db-step',
  templateUrl: './destination-db-step.component.html',
  providers: [DestinationDBStepValueAccessor],
  imports: [
    CommonModule,
    DestinationFolderInputComponent,
    DestinationTableComponent,
    FormGroupComponent,
    FormsModule,
    I18NextPipe,
    InputComponent,
    ReactiveFormsModule,
    SwitcherComponent
  ]
})
export class DestinationDBStepComponent extends BaseForDestinationStep<DestinationDBStepValue> implements OnInit {
  i18nPipe = inject(I18NextPipe);
  mainService: RemoteManagementWizardsService;
  modalService = inject(ModalService);
  stepService = inject(WizardStepsService);

  public readonly elementsSelector = {
    name: { fileDatabaseBlock: 'fileDatabaseBlock', databaseBlock: 'databaseBlock' }
  };
  private readonly restoreDBControls: string[] = ['destinationFolderLog', 'closeConnection', 'overwriteExisting', 'specificList'];
  private readonly restoreFileDBControls: string[] = ['createFolder'];

  public loadingTableData = false;
  public itemsData: { data: RestoreSourceData; items: Partial<DatabaseItem>[] } = { data: null, items: [] };

  get destinationFolderControl(): FormControl<string> {
    return this.stepForm.get('destinationFolder') as FormControl<string>;
  }

  get destinationFolderLogControl(): FormControl<string> {
    return this.stepForm.get('destinationFolderLog') as FormControl<string>;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
    this.setValues();
    this.destinationFolderParams = this.getParamsForDestinationFolder();
    this.switchControlsState();

    if (this.isRestoreDatabase) this.getDataForTable();
  }

  initForm(): void {
    this.stepForm = new FormGroup<DestinationDBStepValueForm>(
      {
        destinationFolder: new FormControl(''),
        destinationFolderLog: new FormControl(''),
        createFolder: new FormControl(false),
        closeConnection: new FormControl(false),
        backupTemplate: new FormControl(this.isRestoreFileDatabase ? DefaultTemplate : DefaultTemplateMask, Validators.required),
        overwriteExisting: new FormControl(false),
        specificList: new FormControl([])
      },
      this.databaseFormValidator.bind(this)
    );

    this.initFormEvents();
  }

  databaseFormValidator(): ValidationErrors | null {
    return this.isRestoreDatabase && (!this.overwriteAndSpecific?.valid || !this.itemsData?.data?.folders?.length)
      ? { specificError: true }
      : null;
  }

  updateForm(value: DestinationDBStepValue): void {
    if (!value) return;

    this.stepForm.reset(value);

    if (this.isRestoreDatabase) {
      this.overwriteAndSpecific = { overwriteExisting: value.overwriteExisting, specificList: value.specificList, valid: true };
    }
  }

  forceValid(): void {
    if (this.isRestoreDatabase && !this.overwriteAndSpecific?.valid) {
      this.forceValidate = true;
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  switchControlsState(): void {
    this.toggleFormControls(this.restoreDBControls, this.isRestoreDatabase);
    this.toggleFormControls(this.restoreFileDBControls, this.isRestoreFileDatabase);
  }

  getDataForTable(): void {
    this.stepService.treeData$
      .pipe(
        filter((data: RestoreSourceData) => this.isRestoreDatabase && !!data?.folders && this.stepForm?.get('specificList').enabled),
        tap(() => (this.loadingTableData = true)),
        switchMap((data: RestoreSourceData) =>
          combineLatest([of(data), this.itemsData?.items?.length ? of(this.itemsData?.items) : this.mainService.dataBases$.asObservable()])
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: ([data, result]: [RestoreSourceData, DatabaseItem[]]) => {
          this.itemsData = { data: data || { folders: [], treeData: [] }, items: result || [] };
          this.loadingTableData = false;
        },
        error: () => (this.loadingTableData = false)
      });
  }

  overwriteAndSpecificChangeHandler(tableData: DestinationTableData): void {
    if (!this.stepForm) return;

    queueMicrotask(() => {
      this.stepForm.get('overwriteExisting').setValue(tableData.overwriteExisting);
      this.stepForm.get('specificList').setValue(tableData.specificList);
      this.forceValidate = false;
    });
  }
}
